import { timeToMinutes, type LocalMoment } from "./dates.js";
import { REMINDER_GRACE_MINUTES } from "./schedule.js";
import { minutesToTime } from "./tasks.js";

/** Used when the onboarding questionnaire didn't ask (or they skipped it). */
export const DEFAULT_MORNING_TIME = "07:30";
export const DEFAULT_EVENING_TIME = "21:00";
const LATEST_EVENING_TIME = "23:30";

/** The morning summary goes out this long after they wake up… */
const MORNING_AFTER_WAKE_MINUTES = 30;
/** …and the end-of-day check this long before they go to sleep. */
const EVENING_BEFORE_SLEEP_MINUTES = 60;

export type DigestKind = "morning" | "evening";

/** When the two daily digests go out, from the wake and sleep times they gave. */
export function digestTimes(profile: { wakeTime: string | null; sleepTime: string | null }): Record<DigestKind, string> {
  return {
    morning: profile.wakeTime
      ? minutesToTime(timeToMinutes(profile.wakeTime) + MORNING_AFTER_WAKE_MINUTES)
      : DEFAULT_MORNING_TIME,
    evening: profile.sleepTime ? eveningTime(profile.sleepTime) : DEFAULT_EVENING_TIME,
  };
}

/**
 * An hour before bed, but on the same calendar day: someone who sleeps at 01:00 gets it at
 * 23:30, not at 00:00 about a day that has just started.
 */
function eveningTime(sleepTime: string): string {
  const minutes = timeToMinutes(sleepTime) - EVENING_BEFORE_SLEEP_MINUTES;
  return minutes < 12 * 60 ? LATEST_EVENING_TIME : minutesToTime(minutes);
}

export function isDigestDue(time: string, now: LocalMoment): boolean {
  const late = now.minutes - timeToMinutes(time);
  return late >= 0 && late <= REMINDER_GRACE_MINUTES;
}

export interface DayItem {
  title: string;
  emoji: string | null;
  /** null = sometime that day. */
  time: string | null;
}

/** "3 hábitos y 2 tareas" — the parts that aren't zero. */
function countLabel(habits: number, tasks: number): string {
  const parts = [];
  if (habits) parts.push(`${habits} ${habits === 1 ? "hábito" : "hábitos"}`);
  if (tasks) parts.push(`${tasks} ${tasks === 1 ? "tarea" : "tareas"}`);
  return parts.join(" y ");
}

const itemLabel = (item: DayItem) => `${item.emoji ? `${item.emoji} ` : ""}${item.title}`;

/** The morning summary, or null on a day with nothing planned (no point waking them). */
export function morningDigest(habits: DayItem[], tasks: DayItem[]): { title: string; body: string } | null {
  if (habits.length + tasks.length === 0) return null;
  const timed = [...habits, ...tasks]
    .filter((i): i is DayItem & { time: string } => i.time !== null)
    .sort((a, b) => a.time.localeCompare(b.time));
  const first = timed[0];
  const lines = [`Hoy tienes ${countLabel(habits.length, tasks.length)}.`];
  if (first) lines.push(`Lo primero: ${first.time} ${itemLabel(first)}.`);
  return { title: "☀️ Tu día de hoy", body: lines.join(" ") };
}

/** The end-of-day check, or null when everything is already marked. */
export function eveningDigest(pendingHabits: number, pendingTasks: number): { title: string; body: string } | null {
  if (pendingHabits + pendingTasks === 0) return null;
  const verb = pendingHabits + pendingTasks === 1 ? "queda" : "quedan";
  const next = pendingTasks > 0 ? "Toca para marcar o pasar las tareas a mañana." : "Toca para marcar cómo te fue.";
  const body = `Te ${verb} ${countLabel(pendingHabits, pendingTasks)} sin marcar. ${next}`;
  return { title: "🌙 Cierre del día", body };
}

/** On Sunday the evening check becomes an invitation to review the week. */
export const WEEKLY_REVIEW_WEEKDAY = 0;

export function weeklyDigest(): { title: string; body: string } {
  return {
    title: "🌙 Cierre de la semana",
    body: "¿Revisamos cómo te fue? Toca para ver tu semana y ajustar la próxima con tu asistente.",
  };
}
